import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { FIELD_GROUPS, PROFILE_FIELDS, STAGES, STAGE_LABELS, formatHeight, formatPhone, type Stage } from '@accheyrishtey/shared';
import { api, ApiError } from '../lib/api';
import type { Note, Photo, Profile } from '../lib/types';
import { formatDate, formatDateTime, initials } from '../lib/format';
import { buildWhatsAppText, whatsAppShareUrl } from '../lib/whatsapp';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { Input, Select, Textarea } from '../components/ui/Field';
import { LoadingState } from '../components/ui/LoadingState';
import { Modal } from '../components/ui/Modal';
import { PhotoGallery } from '../components/profile/PhotoGallery';
import { PdfViewer } from '../components/profile/PdfViewer';

interface Signature { signature: string; timestamp: number; api_key: string; cloud_name: string; folder: string; upload_url: string }

type PhotoShareStatus = 'idle' | 'loading' | 'files' | 'links' | 'none';

interface PhotoSharePreparation {
  status: PhotoShareStatus;
  files: File[];
  explanation: string;
}

const initialPhotoShare: PhotoSharePreparation = {
  status: 'idle',
  files: [],
  explanation: '',
};

const imageExtensions: Record<string, string> = {
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

function photoShareFilename(profileName: string, index: number, mimeType: string): string {
  const safeName = profileName.trim().replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'Profile';
  return `Acchey-Rishtey-${safeName}-${index + 1}.${imageExtensions[mimeType] ?? 'jpg'}`;
}

async function downloadPhotoForShare(
  photo: Photo,
  profileName: string,
  index: number,
  signal: AbortSignal,
): Promise<File> {
  const response = await fetch(photo.url, { credentials: 'omit', signal });
  if (!response.ok) throw new Error('A photograph could not be prepared');
  const blob = await response.blob();
  if (!blob.type.startsWith('image/')) throw new Error('A photograph was not returned as an image');
  return new File([blob], photoShareFilename(profileName, index, blob.type), { type: blob.type });
}

function valueText(profile: Profile, key: string): string {
  const value = profile[key];
  if (value === null || value === undefined || value === '') return 'Not provided';
  if (key === 'height_cm' || key === 'pref_height_cm') return formatHeight(Number(value));
  if (key === 'date_of_birth') return formatDate(String(value));
  if (key.includes('contact_number')) return formatPhone(String(value));
  return String(value);
}

export function ProfileDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteName, setDeleteName] = useState('');
  const [pendingStage, setPendingStage] = useState<Stage | null>(null);
  const [undoStage, setUndoStage] = useState<Stage | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [note, setNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteBody, setEditingNoteBody] = useState('');
  const [pdfOpen, setPdfOpen] = useState(false);
  const closePdf = useCallback(() => setPdfOpen(false), []);
  const [shareOpen, setShareOpen] = useState(false);
  const [photoShare, setPhotoShare] = useState<PhotoSharePreparation>(initialPhotoShare);
  const closeShare = useCallback(() => setShareOpen(false), []);
  const [error, setError] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  const profile = useQuery({ queryKey: ['profile', id], queryFn: () => api.get<Profile>(`/profiles/${id}`), enabled: Boolean(id) });
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['profile', id] }),
      queryClient.invalidateQueries({ queryKey: ['profiles'] }),
      queryClient.invalidateQueries({ queryKey: ['profile-stats'] }),
    ]);
  };
  const patch = useMutation({ mutationFn: (body: Record<string, unknown>) => api.patch<Profile>(`/profiles/${id}`, body), onSuccess: async () => { await refresh(); setEditOpen(false); } });
  const changeStage = useMutation({ mutationFn: (stage: Stage) => api.patch(`/profiles/${id}/stage`, { stage }), onSuccess: refresh });
  const toggleStar = useMutation({ mutationFn: () => api.patch(`/profiles/${id}/star`), onSuccess: refresh });
  const addNote = useMutation({ mutationFn: () => api.post<Note>(`/profiles/${id}/notes`, { body: note }), onSuccess: async () => { setNote(''); await refresh(); } });
  const removeNote = useMutation({ mutationFn: (noteId: string) => api.del(`/notes/${noteId}`), onSuccess: refresh });
  const updateNote = useMutation({ mutationFn: ({ noteId, body }: { noteId: string; body: string }) => api.patch(`/notes/${noteId}`, { body }), onSuccess: async () => { setEditingNoteId(null); await refresh(); } });
  const removePhoto = useMutation({ mutationFn: (photoId: string) => api.del(`/photos/${photoId}`), onSuccess: refresh });
  const makePrimary = useMutation({ mutationFn: (photoId: string) => api.patch(`/photos/${photoId}`, { is_primary: true }), onSuccess: refresh });
  const removeProfile = useMutation({ mutationFn: () => api.del(`/profiles/${id}`), onSuccess: () => navigate('/dashboard') });
  const data = profile.data;
  const primary = useMemo(() => data?.photos.find((item) => item.is_primary) ?? data?.photos[0], [data]);

  useEffect(() => {
    if (!shareOpen || !data) return;
    if (data.photos.length === 0) {
      setPhotoShare({
        status: 'none',
        files: [],
        explanation: 'This profile has no photographs to share.',
      });
      return;
    }
    if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') {
      setPhotoShare({
        status: 'links',
        files: [],
        explanation: 'This browser cannot attach image files. WhatsApp will receive direct links to all photographs instead.',
      });
      return;
    }

    const controller = new AbortController();
    setPhotoShare({
      status: 'loading',
      files: [],
      explanation: `Preparing ${data.photos.length} photograph${data.photos.length === 1 ? '' : 's'} for your device's share sheet…`,
    });

    void Promise.all(data.photos.map((photo, index) => (
      downloadPhotoForShare(photo, data.full_name, index, controller.signal)
    ))).then((files) => {
      if (controller.signal.aborted) return;
      let filesSupported = false;
      try { filesSupported = navigator.canShare({ files }); }
      catch { filesSupported = false; }

      setPhotoShare(filesSupported ? {
        status: 'files',
        files,
        explanation: `Your device can share ${files.length} photograph${files.length === 1 ? '' : 's'}. Choose WhatsApp from the share sheet to pass them on as image files.`,
      } : {
        status: 'links',
        files: [],
        explanation: 'This browser cannot share these image files. WhatsApp will receive direct links to all photographs instead.',
      });
    }).catch((reason: unknown) => {
      if (controller.signal.aborted || (reason instanceof DOMException && reason.name === 'AbortError')) return;
      setPhotoShare({
        status: 'links',
        files: [],
        explanation: 'The photograph files could not be prepared. WhatsApp will receive direct links to all photographs instead.',
      });
    });

    return () => controller.abort();
  }, [shareOpen, data]);

  const beginEdit = () => {
    if (!data) return;
    setDraft(Object.fromEntries(PROFILE_FIELDS.map((field) => [field.key, data[field.key] ?? ''])));
    setEditOpen(true);
  };
  const save = () => {
    const numbers = new Set(['height_cm', 'number_of_sisters', 'number_of_brothers', 'pref_age_min', 'pref_age_max', 'pref_height_cm']);
    const body = Object.fromEntries(Object.entries(draft).map(([key, value]) => [key, numbers.has(key) && value !== '' ? Number(value) : value]));
    patch.mutate(body);
  };
  const addPhotos = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []).slice(0, Math.max(0, 6 - (data?.photos.length ?? 0)));
    if (!files.length) return;
    setPhotoBusy(true); setError('');
    try {
      for (const file of files) {
        const signature = await api.post<Signature>(`/profiles/${id}/photos/signature`);
        const form = new FormData();
        form.append('file', file); form.append('api_key', signature.api_key); form.append('timestamp', String(signature.timestamp)); form.append('signature', signature.signature); form.append('folder', signature.folder);
        const uploaded = await fetch(signature.upload_url, { method: 'POST', body: form });
        if (!uploaded.ok) throw new Error('Photograph upload failed');
        const result = await uploaded.json() as { public_id: string; secure_url: string; width: number; height: number; bytes: number };
        await api.post(`/profiles/${id}/photos`, { public_id: result.public_id, url: result.secure_url, width: result.width, height: result.height, bytes: result.bytes });
      }
      await refresh();
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not add photographs.'); }
    finally { setPhotoBusy(false); event.target.value = ''; }
  };
  const beginShare = () => {
    setPhotoShare(initialPhotoShare);
    setShareOpen(true);
  };
  const openWhatsApp = (includePhotos: boolean) => {
    window.open(
      whatsAppShareUrl(data!, { includePhotos }),
      '_blank',
      'noopener,noreferrer',
    );
    setShareOpen(false);
  };
  const shareWithPhotos = async () => {
    if (!data || data.photos.length === 0 || photoShare.status === 'loading' || photoShare.status === 'idle') return;
    if (photoShare.status !== 'files') {
      openWhatsApp(true);
      return;
    }

    try {
      await navigator.share({
        title: `${data.full_name} - Acchey Rishtey`,
        text: buildWhatsAppText(data),
        files: photoShare.files,
      });
      setShareOpen(false);
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === 'AbortError') return;
      setPhotoShare({
        status: 'links',
        files: [],
        explanation: 'Your device could not start file sharing. Click “With photos” again to open WhatsApp with direct photograph links instead.',
      });
    }
  };
  const applyStage = async (next: Stage, offerUndo = true) => {
    const previous = data?.stage;
    setPendingStage(null);
    await changeStage.mutateAsync(next);
    if (offerUndo && previous && previous !== next) setUndoStage(previous);
  };

  if (profile.isLoading) return <LoadingState rows={3}/>;
  if (!data || profile.isError) return <div className="empty-state"><h1>Profile not found</h1><Button onClick={() => navigate('/dashboard')}>Back to profiles</Button></div>;

  return <div className="profile-detail page-enter">
    <button className="back-link" onClick={() => navigate('/dashboard')}><Icon name="back"/>Profiles</button>
    {error ? <div className="alert alert--error">{error}</div> : null}
    <section className="profile-hero">
      <div className="profile-hero__portrait">{primary ? <img src={primary.url} alt={`${data.full_name}'s primary photograph`}/> : <span>{initials(data.full_name)}</span>}</div>
      <div className="profile-hero__main"><p className="eyebrow">Matrimony profile</p><h1>{data.full_name}</h1><p>{data.age} years · {formatHeight(data.height_cm) || 'Height not provided'} · {data.gender} · {data.marital_status}</p><span className={`stage stage--${data.stage}`}>{STAGE_LABELS[data.stage]}</span></div>
      <div className="profile-hero__actions">
        <Select aria-label="Profile stage" value={data.stage} onChange={(event) => { const next = event.target.value as Stage; if (next === 'married') setPendingStage(next); else void applyStage(next); }}>{STAGES.map((stage) => <option key={stage} value={stage}>{STAGE_LABELS[stage]}</option>)}</Select>
        <Button variant="secondary" icon={<Icon name="star" fill={data.is_starred ? 'currentColor' : 'none'}/>} onClick={() => toggleStar.mutate()}>{data.is_starred ? 'Starred' : 'Star'}</Button>
        <Button icon={<Icon name="edit"/>} onClick={beginEdit}>Edit profile</Button>
      </div>
    </section>

    <div className="detail-layout"><main>
      {FIELD_GROUPS.map((group) => <section className="detail-section" key={group.key}><h2>{group.label}</h2><dl>{PROFILE_FIELDS.filter((field) => field.group === group.key).map((field) => { const rendered = valueText(data, field.key); return <div key={field.key}><dt>{field.label}</dt><dd>{field.input === 'phone' && rendered !== 'Not provided' ? <a className="detail-phone" href={`tel:${String(data[field.key])}`}>{rendered}</a> : <span className={rendered === 'Not provided' ? 'muted' : ''}>{rendered}</span>}</dd></div>; })}</dl></section>)}
      <section className="detail-section"><div className="section-heading"><h2>Photographs</h2><label className={`button button--secondary ${photoBusy || data.photos.length >= 6 ? 'is-disabled' : ''}`}><Icon name="camera"/><span>{photoBusy ? 'Uploading…' : 'Add photographs'}</span><input type="file" accept="image/*" multiple hidden disabled={photoBusy || data.photos.length >= 6} onChange={addPhotos}/></label></div>
        <PhotoGallery photos={data.photos} onPrimary={(photoId) => makePrimary.mutate(photoId)} onRemove={(photoId) => removePhoto.mutate(photoId)}/>
      </section>
    </main><aside className="detail-aside">
      <section className="action-panel"><h2>Share profile</h2><Button variant="secondary" icon={<Icon name="share"/>} onClick={beginShare}>Share on WhatsApp</Button><Button variant="ghost" icon={<Icon name="eye"/>} onClick={() => setPdfOpen(true)}>View PDF</Button></section>
      <section className="notes-panel"><h2>Private notes <span>{data.notes.length}</span></h2><Textarea value={note} onChange={(event) => setNote(event.target.value)} onKeyDown={(event) => { if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && note.trim()) addNote.mutate(); }} placeholder="Add a private note…" rows={3}/><small>Notes are private to you, and are searched by the dashboard search box.</small><Button onClick={() => addNote.mutate()} disabled={!note.trim()} loading={addNote.isPending}>Add note</Button><div className="note-timeline">{data.notes.map((item) => <article key={item.id}>{editingNoteId === item.id ? <><Textarea value={editingNoteBody} onChange={(event) => setEditingNoteBody(event.target.value)} onKeyDown={(event) => { if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && editingNoteBody.trim()) updateNote.mutate({ noteId: item.id, body: editingNoteBody }); }}/><div className="note-edit-actions"><button onClick={() => setEditingNoteId(null)}>Cancel</button><button onClick={() => updateNote.mutate({ noteId: item.id, body: editingNoteBody })}>Save</button></div></> : <p>{item.body}</p>}<footer><time dateTime={item.created_at} title={formatDateTime(item.created_at)}>{formatDateTime(item.created_at)}</time><span><button onClick={() => { setEditingNoteId(item.id); setEditingNoteBody(item.body); }} aria-label="Edit note"><Icon name="edit"/></button><button onClick={() => removeNote.mutate(item.id)} aria-label="Delete note"><Icon name="trash"/></button></span></footer></article>)}</div></section>
      <section className="record-panel"><small>Submitted {formatDateTime(String(data.submitted_at ?? ''))}</small><small>Last updated {formatDateTime(String(data.updated_at ?? ''))}</small><Button variant="danger" icon={<Icon name="trash"/>} onClick={() => { setDeleteName(''); setDeleteOpen(true); }}>Delete profile</Button></section>
    </aside></div>

    <Modal open={editOpen} onClose={() => setEditOpen(false)} title={`Edit ${data.full_name}`} wide><div className="edit-form">{FIELD_GROUPS.map((group) => <fieldset key={group.key}><legend>{group.label}</legend><div className="form-grid">{PROFILE_FIELDS.filter((field) => field.group === group.key).map((field) => <label className={field.input === 'textarea' ? 'field field--wide' : 'field'} key={field.key}>{field.label}{field.input === 'textarea' ? <Textarea value={String(draft[field.key] ?? '')} onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}/> : field.input === 'select' ? <Select value={String(draft[field.key] ?? '')} onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}><option value="">Not provided</option>{field.options?.map((option) => <option key={option}>{option}</option>)}</Select> : <Input type={field.input === 'date' ? 'date' : ['number','height'].includes(field.input) ? 'number' : 'text'} value={String(draft[field.key] ?? '').slice(0, field.input === 'date' ? 10 : undefined)} onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}/>}</label>)}</div></fieldset>)}</div><div className="modal-actions"><Button variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button><Button onClick={save} loading={patch.isPending}>Save changes</Button></div>{patch.error ? <div className="alert alert--error">{patch.error instanceof ApiError ? patch.error.message : 'Could not save changes.'}</div> : null}</Modal>
    <Modal open={shareOpen} onClose={closeShare} title="Share on WhatsApp">
      <div className="share-choice">
        <p>Every filled profile field will be included. Private notes and administrator-only record details are never shared.</p>
        <div className="share-choice__options">
          <Button variant="secondary" icon={<Icon name="share"/>} onClick={() => openWhatsApp(false)}>Without photos</Button>
          <Button
            icon={<Icon name="camera"/>}
            onClick={() => void shareWithPhotos()}
            disabled={photoShare.status === 'idle' || photoShare.status === 'loading' || photoShare.status === 'none'}
          >{photoShare.status === 'idle' || photoShare.status === 'loading' ? 'Preparing photographs…' : 'With photos'}</Button>
        </div>
        <div className={`share-choice__status share-choice__status--${photoShare.status}`} role="status" aria-live="polite">
          {photoShare.status === 'loading' ? <span className="spinner" aria-hidden="true"/> : <Icon name={photoShare.status === 'files' ? 'camera' : 'link'}/>}
          <span>{photoShare.explanation || 'Checking how this device can share photographs…'}</span>
        </div>
        <p className="share-choice__privacy">After sharing, recipients may save or forward the profile and photographs. Please choose the recipient carefully.</p>
      </div>
      <div className="modal-actions"><Button variant="ghost" onClick={closeShare}>Cancel</Button></div>
    </Modal>
    <Modal open={pendingStage === 'married'} onClose={() => setPendingStage(null)} title="Mark this profile as married?"><p>The profile will move out of the Active list and into Married. You can change it back later.</p><div className="modal-actions"><Button variant="ghost" onClick={() => setPendingStage(null)}>Cancel</Button><Button onClick={() => void applyStage('married')} loading={changeStage.isPending}>Mark as married</Button></div></Modal>
    <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete this profile?"><p>This permanently removes the profile, its notes, and its photographs. This cannot be undone.</p><label className="field">Type <strong>{data.full_name}</strong> to confirm<Input value={deleteName} onChange={(event) => setDeleteName(event.target.value)}/></label><div className="modal-actions"><Button variant="ghost" onClick={() => setDeleteOpen(false)}>Cancel</Button><Button variant="danger" disabled={deleteName !== data.full_name} onClick={() => removeProfile.mutate()} loading={removeProfile.isPending}>Delete permanently</Button></div></Modal>
    <PdfViewer profile={data} open={pdfOpen} onClose={closePdf}/>
    {undoStage ? <div className="undo-toast" role="status">Stage changed.<button onClick={() => { void applyStage(undoStage, false); setUndoStage(null); }}>Undo</button><button aria-label="Dismiss" onClick={() => setUndoStage(null)}><Icon name="close"/></button></div> : null}
  </div>;
}
